import { ApplicationRef, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { FuseConfirmationService } from '@fleet/fuse';

import { FleetNavigationService } from '@fleet/navigation';
import { BehaviorSubject, concat, interval } from 'rxjs';
@Injectable({
  providedIn: 'root',
})
export class VersionUpdateService {
  isProduction: boolean;
  fleetProduct: string;
  showUpdateAvailable: BehaviorSubject<any> = new BehaviorSubject(false);
  updateAvailable: BehaviorSubject<any> = new BehaviorSubject(false);
  constructor(
    private appRef: ApplicationRef,
    private updates: SwUpdate,
    private fuseConfirmationService: FuseConfirmationService,
    private router: Router,
    private fleetNavigationService: FleetNavigationService,
    @Inject('env') env: any
  ) {
    this.fleetProduct = env.fleetProduct;
    this.isProduction = env.production;

    console.log(
      '%c🔄 Version Update Service',
      'background: linear-gradient(45deg, #667eea 0%, #764ba2 100%); color: white; padding: 8px 12px; border-radius: 4px; font-weight: bold;',
      `Initialized for ${this.fleetProduct} (${
        this.isProduction ? 'Production' : 'Development'
      })`
    );
  }

  get showUpdateAvailable$() {
    return this.showUpdateAvailable.asObservable();
  }

  get updateAvailable$() {
    return this.updateAvailable.asObservable();
  }

  initialise() {
    console.log(
      '%c⚡ Version Update Service',
      'background: #4ade80; color: white; padding: 6px 10px; border-radius: 4px; font-weight: bold;',
      'Starting update monitoring...'
    );

    const appIsStable$ = this.appRef.isStable; //.pipe(first((isStable) => isStable === true));
    const everyHour$ = interval(1 * 60 * 60 * 1000);
    const every5mins$ = interval(300000);

    const every5minsOnceAppIsStable$ = concat(appIsStable$, every5mins$);
    //   const everyHourOnceAppIsStable$ = concat(appIsStable$, everyHour$);

    every5mins$.subscribe(() => {
      if (this.isProduction) {
        console.log(
          '%c🔍 Checking for updates...',
          'background: #3b82f6; color: white; padding: 4px 8px; border-radius: 4px;',
          new Date().toLocaleTimeString()
        );
        this.updates.checkForUpdate();
      }
    });

    //if new update prompt user to refresh
    this.updates.versionUpdates.subscribe((event: VersionEvent) => {
      if (event.type === 'VERSION_READY') {
        console.log(
          '%c🎉 New Version Available!',
          'background: linear-gradient(45deg, #f59e0b 0%, #d97706 100%); color: white; padding: 8px 12px; border-radius: 4px; font-weight: bold;',
          `Version: ${event.latestVersion.hash}`
        );

        if (this.fleetProduct === 'HUB') {
          console.log(
            '%c📱 HUB App Update',
            'background: #8b5cf6; color: white; padding: 4px 8px; border-radius: 4px;',
            'Setting update available flag'
          );
          this.updateAvailable.next(true);
          this.showUpdateAvailable.next(true);
        } else {
          console.log(
            '%c💬 Showing Update Dialog',
            'background: #06b6d4; color: white; padding: 4px 8px; border-radius: 4px;',
            `Product: ${this.fleetProduct}`
          );

          const dialog = this.fuseConfirmationService.open({
            title: 'Important Update',
            message: `There is a new version of this app`,
            icon: {
              show: true,
              name: 'heroicons_outline:badge-check',
              color: 'success',
            },
            dismissible: true,
            actions: {
              confirm: { show: true, label: 'UPDATE', color: 'primary' },
              cancel: { show: false },
            },
          });

          dialog.afterClosed().subscribe({
            next: () => {
              console.log(
                '%c🔄 Activating Update',
                'background: #10b981; color: white; padding: 4px 8px; border-radius: 4px;',
                'User confirmed update'
              );
              this.updates.activateUpdate().then(() => {
                console.log(
                  '%c♻️ Reloading Application',
                  'background: #ef4444; color: white; padding: 4px 8px; border-radius: 4px;',
                  'Update activated successfully'
                );
                if (this.fleetNavigationService.flexNavigation) {
                  const currentPath = this.router.url;
                  const separator = currentPath.includes('?') ? '&' : '?';
                  document.location.href = `${currentPath}${separator}layout=flex`;
                } else {
                  document.location.reload();
                }
              });
            },
          });
        }
      } else if (event.type === 'VERSION_DETECTED') {
        console.log(
          '%c🔍 Version Check',
          'background: #6b7280; color: white; padding: 4px 8px; border-radius: 4px;',
          'New version detected, downloading...'
        );
      } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
        console.error(
          '%c❌ Update Failed',
          'background: #ef4444; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
          'Version installation failed:',
          event.error
        );
      }
    });

    this.updates.unrecoverable.subscribe((event) => {
      console.error(
        '%c💥 Unrecoverable Error!',
        'background: linear-gradient(45deg, #dc2626 0%, #991b1b 100%); color: white; padding: 8px 12px; border-radius: 4px; font-weight: bold;',
        'Forcing app reload:',
        event
      );
      // Sentry.captureException(
      //   'Unrecoverable app version issue: \n' + JSON.stringify(event)
      // );
      if (this.fleetNavigationService.flexNavigation) {
        const currentPath = this.router.url;
        const separator = currentPath.includes('?') ? '&' : '?';
        document.location.href = `${currentPath}${separator}layout=flex`;
      } else {
        document.location.reload();
      }
    });

    console.log(
      '%c✅ Version Update Service Ready',
      'background: #059669; color: white; padding: 6px 10px; border-radius: 4px; font-weight: bold;',
      'All listeners initialized'
    );
  }

  dismissUpdate() {
    console.log(
      '%c👋 Update Dismissed',
      'background: #6b7280; color: white; padding: 4px 8px; border-radius: 4px;',
      'User dismissed the update notification'
    );
    this.showUpdateAvailable.next(false);
  }

  activateUpdate() {
    console.log(
      '%c🚀 Manual Update Activation',
      'background: #7c3aed; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
      'User manually triggered update'
    );
    this.updates.activateUpdate().then(() => {
      console.log(
        '%c♻️ Reloading Application',
        'background: #ef4444; color: white; padding: 4px 8px; border-radius: 4px;',
        'Manual update activated successfully'
      );
      document.location.reload();
    });
  }
}
